/** Cumulative bands from a free allowance and the paid tiers: `[upToUnits, pricePer1000Micros]` pairs (a price per 1,000 units in micros IS the price of one unit in micros), the last open. */
function bands(free, tiers) {
    return [...(free === null ? [] : [{ upToUnits: free, unitPriceMicros: 0 }]), ...tiers.map(([upToUnits, unitPriceMicros]) => ({ upToUnits, unitPriceMicros }))];
}
const sku = (unit, b) => ({ unit, bands: b });
// Google: the first band is the free cap per SKU per month (Essentials 10,000, Pro 5,000, Enterprise 1,000); tiers close at 100k, 500k, 1M, 5M events and then stay open.
const googleTiers = (a, b, c, d, e) => [[100_000, a], [500_000, b], [1_000_000, c], [5_000_000, d], [null, e]];
const GOOGLE_ESSENTIALS = bands(10_000, googleTiers(5_000, 4_000, 3_000, 1_500, 380));
const GOOGLE_PRO = bands(5_000, googleTiers(10_000, 8_000, 6_000, 3_000, 750));
const GOOGLE_ENTERPRISE = bands(1_000, googleTiers(15_000, 12_000, 9_000, 4_500, 1_140));
const FREE_ALWAYS = bands(null, [[null, 0]]);
// HERE: tiers close at 5M, 10M, 20M and 50M transactions (routing and geocoding) or 200k, 1M, 5M and 20M (the dearer SKUs).
const HERE_CHEAP = bands(30_000, [[5_000_000, 880], [10_000_000, 700], [20_000_000, 610], [50_000_000, 270], [null, 180]]);
const HERE_DEAR = bands(5_000, [[200_000, 2_920], [1_000_000, 2_330], [5_000_000, 2_050], [20_000_000, 880], [null, 580]]);
const HERE_MATRIX = bands(2_500, [[200_000, 5_830], [1_000_000, 4_660], [5_000_000, 4_080], [20_000_000, 1_750], [null, 1_170]]);
export const BRAZIL_GEO_PRICE_BOOK_EFFECTIVE_FROM = '2026-11-01T00:00:00Z';
export const BRAZIL_GEO_PRICE_BOOK_V1_PROPOSAL = {
    vendors: {
        google: {
            currency: 'USD',
            billingTimeZone: 'America/Los_Angeles',
            sources: [
                { url: 'https://developers.google.com/maps/billing-and-pricing/pricing', readAt: '2026-10-31' },
                { url: 'https://developers.google.com/maps/documentation/places/web-service/session-pricing', readAt: '2026-10-31' },
            ],
            skus: {
                'google.routes.compute.essentials': sku('request', GOOGLE_ESSENTIALS),
                'google.routes.compute.pro': sku('request', GOOGLE_PRO),
                'google.routes.compute.enterprise': sku('request', GOOGLE_ENTERPRISE),
                'google.routes.matrix.essentials': sku('element', GOOGLE_ESSENTIALS),
                'google.routes.matrix.pro': sku('element', GOOGLE_PRO),
                'google.routes.matrix.enterprise': sku('element', GOOGLE_ENTERPRISE),
                'google.geocoding': sku('request', GOOGLE_ESSENTIALS),
                'google.places.autocomplete_requests': sku('request', bands(10_000, googleTiers(2_830, 2_270, 1_700, 850, 210))),
                'google.places.autocomplete_session_usage': sku('session_request', FREE_ALWAYS),
                'google.places.details.ids_only': sku('request', FREE_ALWAYS),
                'google.places.details.essentials': sku('request', GOOGLE_ESSENTIALS),
                'google.places.details.pro': sku('request', bands(5_000, googleTiers(17_000, 13_600, 10_200, 5_100, 1_280))),
                'google.places.details.enterprise': sku('request', bands(1_000, googleTiers(20_000, 16_000, 12_000, 6_000, 1_510))),
                'google.places.details.enterprise_atmosphere': sku('request', bands(1_000, googleTiers(25_000, 20_000, 15_000, 7_500, 2_280))),
                'google.places.text_search.pro': sku('request', bands(5_000, googleTiers(32_000, 25_600, 19_200, 9_600, 2_400))),
                'google.distance_matrix.basic': sku('element', bands(10_000, [[100_000, 5_000], [null, 4_000]])),
            },
        },
        here: {
            currency: 'USD',
            billingTimeZone: 'UTC',
            sources: [
                { url: 'https://www.here.com/get-started/pricing/supplemental-pricing', readAt: '2026-10-31' },
                { url: 'https://www.here.com/learn/blog/time-aware-route-routing-v8', readAt: '2026-10-31' },
            ],
            skus: {
                'here.routing.car': sku('transaction', HERE_CHEAP),
                'here.routing.time_aware': sku('transaction', HERE_DEAR),
                'here.routing.scooter': sku('transaction', HERE_DEAR),
                'here.geocode': sku('transaction', HERE_CHEAP),
                'here.discover': sku('transaction', HERE_DEAR),
                'here.matrix': sku('transaction', HERE_MATRIX),
            },
        },
        // The self-hosted servers have no vendor bill: one band at price zero (their cost is the server's, fixed, outside this model).
        osrm: { currency: 'USD', billingTimeZone: 'UTC', sources: [], skus: { 'osrm.routing': sku('request', FREE_ALWAYS), 'osrm.matrix': sku('element', FREE_ALWAYS) } },
        nominatim: { currency: 'USD', billingTimeZone: 'UTC', sources: [], skus: { 'nominatim.geocoding': sku('request', FREE_ALWAYS) } },
    },
};
/** v1 PROPOSAL (not published) of `geo.spendCaps`: figures to start from, for the owner to set. The quote does not fall to HERE by itself. */
export const BRAZIL_GEO_SPEND_CAPS_V1_PROPOSAL = {
    currency: 'USD',
    monthlyMinor: 20_000,
    perVendorMonthlyMinor: { google: 15_000, here: 5_000 },
    alertPercents: [50, 80, 100],
    quoteFallsToHereAtCap: false,
};
/** v1 PROPOSAL (not published) of `fx.source` for Brazil: the Banco Central PTAX selling rate, the ECB reference rates as the fallback. */
export const BRAZIL_FX_SOURCE_V1_PROPOSAL = {
    pairs: [{ from: 'USD', to: 'BRL' }],
    primary: { id: 'bcb_ptax', field: 'sell' },
    fallbacks: [{ id: 'ecb_reference' }],
    maxStaleDays: 5,
};
//# sourceMappingURL=maps-spend.js.map